import LoadingScreen from "../../components/LoadingScreen";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { setupUser as setupUserService, verifyUser as verifyUserService } from "../../services/userService";
import { useEffect, useState } from "react";
import { Card, CardContent, TextField, FormControl, Checkbox, FormControlLabel, FormGroup, Button, Box, Alert, useTheme } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import logo from "../../images/ontraq_healthfirst.png";
import { addError } from "../../redux/alerts";
import { useAppDispatch } from "../../redux/store";
import AlertDisplay from "../../components/AlertDisplay";

interface FormData {
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  emailNotifications: boolean;
  termsAgreed: boolean;
}

interface FormErrors {
  password?: string[];
  confirmPassword?: string[];
  phoneNumber?: string[];
  termsAgreed?: string[];
  general?: string[];
}

const VerifyUser = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    emailNotifications: false,
    termsAgreed: false
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (password: string): string[] => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    return errors;
  };

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    const digitsOnly = phoneNumber.replace(/-/g, '');
    return digitsOnly.length === 10 && /^\d+$/.test(digitsOnly);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors;
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }
    
    if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = ['Please enter a valid 10-digit phone number'];
    }
    
    if (!formData.termsAgreed) {
      newErrors.termsAgreed = ['You must agree to the Privacy Policy and Terms and Conditions'];
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field as keyof FormErrors]: undefined
      }));
    }
  };

  const handleSignUp = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const user = {
      customerNumber: params.get('customerNumber')!,
      userId: params.get('token')!,
      password: formData.password,
      phone: formData.phoneNumber,
      emailOption: formData.emailNotifications,
      textOption: false // Update if we want to add text option
    };
    
    try {
      await setupUserService(user);
      
      window.location.href = '/'; // this will force AppInitializer to load the session
    } catch (error) {
      console.error('Signup error:', error);
      dispatch(addError('An error occurred during signup. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyUser = async () => {
    if (params.get('token') && params.get('customerNumber')) {
      try {
        const token = params.get('token');
        const response = await verifyUserService(token!);

        setIsLoading(false);

        if (response.verified) {
          window.location.href = '/'; // this will force AppInitializer to load the session
        }
      } catch (error) {
        console.error(error);
        navigate('/support');
      }
    } else {
      navigate('/support');
    }
  }

  useEffect(() => {
    verifyUser();
  }, []);

  return (
    isLoading ? <LoadingScreen /> : (
      <>
        <div className="navbar-container">
          <img src={logo} style={{ width: '150px' }} alt="company logo" />
        </div>
        <Card variant="outlined" sx={{ m: 2, color: theme.palette.color50555b.main }}>
          <div className="box-header bold">OnTraq Account Setup</div>
          <CardContent sx={{ p: 2 }}>
            <p style={{ fontSize: '14px' }}>Welcome to OnTraq. Before you can log in and use this site, you must complete this registration process. Don't worry! You'll be able to edit what you enter here later in the Account Settings section.</p>
            <h4>Account Information</h4>
            <div style={{ marginBottom: '24px', maxWidth: '600px' }}>
              <TextField 
                label="Password *" 
                placeholder="Password" 
                variant="outlined" 
                fullWidth 
                size="small"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={!!errors.password}
                helperText={errors.password ? errors.password.join(', ') : 'Password must be at least 8 characters with uppercase, lowercase, number, and special character'}
              />
            </div>
            <div style={{ marginBottom: '24px', maxWidth: '600px' }}>
              <TextField 
                label="Confirm Your Password *" 
                placeholder="Confirm Your Password" 
                variant="outlined" 
                fullWidth 
                size="small"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword ? errors.confirmPassword.join(', ') : ''}
              />
            </div>
            <h4>Communication Preferences</h4>
            <FormControl fullWidth sx={{ maxWidth: '600px' }}>
              <TextField 
                label="Phone Number" 
                placeholder="Phone Number" 
                variant="outlined" 
                fullWidth 
                size="small"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber ? errors.phoneNumber.join(', ') : 'Optional - Enter 10-digit phone number'}
              />
            </FormControl>
            <h4>OnTraq sends you reminders and notifications about things happening in your practice. Please tell us where you'd like to receive these messages:</h4>
            <FormGroup sx={{ gap: '16px', ml: '8px' }}>
              <FormControlLabel 
                control={
                  <Checkbox 
                    sx={{ mr: 1 }} 
                    checked={formData.emailNotifications}
                    onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                  />
                } 
                label="Send via email address" 
              />
              <FormControlLabel 
                control={
                  <Checkbox 
                    sx={{ mr: 1 }} 
                    checked={formData.termsAgreed}
                    onChange={(e) => handleInputChange('termsAgreed', e.target.checked)}
                  />
                } 
                label="* I have read and agree to the Privacy Policy and Terms and Conditions of HealthFirst and OnTraq" 
              />
              {errors.termsAgreed && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.termsAgreed.join(', ')}
                </Alert>
              )}
            </FormGroup>
            
            {/* General error display */}
            {errors.general && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {errors.general.join(', ')}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                sx={{ mt: '24px', width: '200px' }} 
                variant="contained" 
                color="primary"
                onClick={handleSignUp}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Setting up...' : 'Sign Up'} 
                <CheckIcon sx={{ fontSize: '16px', ml: '8px' }} />
              </Button>
            </Box>
          </CardContent>
        </Card>
        <AlertDisplay position="top-right" maxMessages={5} autoDismiss={true} />
      </>
    )
  );
};

export default VerifyUser;